
import { supabase } from "@/integrations/supabase/client";
import { detectAccommodationType } from './mappings/AccommodationMapping';
import { detectExperienceType } from './mappings/ExperienceMapping';
import { detectLocation } from './mappings/LocationMapping';
import { applyLocationFilter } from './filters/LocationFilter';
import { applyAccommodationFilter } from './filters/AccommodationFilter';
import { applyExperienceFilter } from './filters/ExperienceFilter';
import { detectPriceConstraints, applyPriceFilter } from './filters/PriceFilter';
import { detectFacilities, applyFacilitiesFilter } from './filters/FacilitiesFilter';
import { logDetectionSteps } from './utils/SearchOrchestration';
import { verifySearchWithSample } from './utils/SearchVerification';
import { verifyAndFilterResults } from './utils/ResultsVerification';

export const performBasicSearch = async (query: string) => {
  try {
    console.log('🔍 === PERFORMING HIERARCHICAL BASIC SEARCH ===');
    console.log('Original query:', `"${query}"`);
    
    const searchTerms = query.toLowerCase().split(' ');
    console.log('Split search terms:', searchTerms);

    // ======= STEP 1-3: DETECTION PHASE =======
    const detectedLocation = detectLocation(searchTerms);
    const priceConstraints = detectPriceConstraints(searchTerms);
    const detectedFacilities = detectFacilities(searchTerms);
    
    logDetectionSteps(detectedLocation, priceConstraints, detectedFacilities);
    
    if (!detectedLocation) {
      console.log('❌ No location detected - cannot proceed with basic search');
      return [];
    }

    // ======= IMPROVED COSTA BRAVA LOGIC =======
    // Special handling for Costa Brava searches to get more results
    if (detectedLocation.type === 'region' && detectedLocation.info.region === 'Costa Brava') {
      console.log('🏖️ === COSTA BRAVA REGION SEARCH - USING BROADER APPROACH ===');
      
      let dbQuery = supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .eq('country', 'Spanje')
        .or('region.ilike.%Costa Brava%,city.ilike.%Costa Brava%,description.ilike.%Costa Brava%');

      // Apply additional filters
      if (priceConstraints) {
        console.log('💰 === APPLYING PRICE FILTERS ===');
        dbQuery = applyPriceFilter(dbQuery, priceConstraints);
      }

      if (detectedFacilities) {
        console.log('🏨 === APPLYING FACILITIES FILTERS ===');
        dbQuery = applyFacilitiesFilter(dbQuery, detectedFacilities);
      }

      // Execute query
      const { data, error } = await dbQuery.limit(100);
      
      if (error) {
        console.error('❌ Costa Brava search error:', error);
        throw error;
      }
      
      console.log(`🏖️ Costa Brava search found ${data?.length || 0} results`);
      return verifyAndFilterResults(data || [], detectedLocation, priceConstraints, detectedFacilities, query);
    }

    // ======= STEP 4: BUILD BASE QUERY =======
    console.log('🔧 === STEP 4: BUILDING BASE QUERY ===');
    let dbQuery = supabase
      .from('Complete feed')
      .select('*')
      .not('imageURL', 'is', null)
      .not('name', 'is', null);

    console.log('✅ Base query initialized');

    // ======= STEP 5-7: APPLY FILTERS =======
    console.log('🎯 === STEP 5: APPLYING HIERARCHICAL LOCATION FILTER ===');
    dbQuery = applyLocationFilter(dbQuery, detectedLocation);
    
    if (priceConstraints) {
      console.log('💰 === STEP 6: APPLYING PRICE FILTERS ===');
      dbQuery = applyPriceFilter(dbQuery, priceConstraints);
    } else {
      console.log('💰 === STEP 6: NO PRICE FILTERS DETECTED ===');
    }

    if (detectedFacilities) {
      console.log('🏨 === STEP 7: APPLYING FACILITIES FILTERS ===');
      dbQuery = applyFacilitiesFilter(dbQuery, detectedFacilities);
    } else {
      console.log('🏨 === STEP 7: NO FACILITIES FILTERS DETECTED ===');
    }

    // ======= STEP 8: VERIFY WITH SAMPLE =======
    await verifySearchWithSample(detectedLocation, priceConstraints, detectedFacilities);

    // ======= STEP 9-10: ADDITIONAL FILTERS =======
    console.log('🔍 === STEP 9: DETECTING OTHER FILTERS ===');
    const detectedAccommodationType = detectAccommodationType(searchTerms);
    console.log('Accommodation detection result:', detectedAccommodationType);
    
    const detectedExperienceType = detectExperienceType(searchTerms);
    console.log('Experience detection result:', detectedExperienceType);

    console.log('🔧 === STEP 10: APPLYING ADDITIONAL FILTERS ===');
    if (detectedAccommodationType) {
      console.log('🏨 Applying accommodation filter within hierarchy...');
      dbQuery = applyAccommodationFilter(dbQuery, detectedAccommodationType);
    }

    if (detectedExperienceType) {
      if (detectedLocation.type === 'city') {
        console.log('🚫 Skipping experience filter for city search to maintain strict hierarchy');
      } else {
        console.log('🏖️ Applying experience filter within hierarchy...');
        dbQuery = applyExperienceFilter(dbQuery, detectedExperienceType);
      }
    }

    // ======= STEP 11: EXECUTE AND VERIFY RESULTS =======
    console.log('⚡ === STEP 11: EXECUTING FINAL HIERARCHICAL QUERY ===');
    const { data, error } = await dbQuery.limit(100);
    
    if (error) {
      console.error('❌ Database error:', error);
      throw error;
    }
    
    return verifyAndFilterResults(data || [], detectedLocation, priceConstraints, detectedFacilities, query);
    
  } catch (error: any) {
    console.error('💥 Hierarchical search error:', error);
    throw error;
  }
};
